import {
  FILE_DEFAULTS,
  FILE_DYNAMICS,
  FILE_ENVS
} from "./chunk-VQRJY4AQ.mjs";
import {
  Config,
  ConfigResource,
  mapFromKeyValues,
  mapToKeyValues,
  validateConfig
} from "./chunk-ZY4D4E3Q.mjs";

// src/index.ts
import * as defs from "@dxos/protocols/proto/dxos/config";

// src/config-service.ts
import * as FileSystem from "@effect/platform/FileSystem";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import { dirname } from "@dxos/node-std/path";
import * as Yaml from "yaml";
import { DX_CONFIG, DX_DATA, getProfileConfigPath, getProfilePath } from "@dxos/client-protocol";
import { invariant } from "@dxos/invariant";
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/config/src/config-service.ts";
var memoryConfig = new Config({
  runtime: {
    client: {
      edgeFeatures: {
        echoReplicator: true,
        feedReplicator: true,
        signaling: true,
        agents: true
      }
    }
  }
});
var defaultConfig = new Config({
  runtime: {
    client: {
      edgeFeatures: {
        echoReplicator: true,
        feedReplicator: true,
        signaling: true,
        agents: true
      },
      storage: {
        persistent: true
      }
    },
    services: {
      edge: {
        url: "wss://edge-production.dxos.workers.dev/"
      },
      iceProviders: [
        {
          urls: "https://edge-production.dxos.workers.dev/ice"
        }
      ],
      ai: {
        server: "https://ai-service.dxos.workers.dev"
      },
      ipfs: {
        server: "https://api.ipfs.dxos.network/api/v0",
        gateway: "https://gateway.ipfs.dxos.network/ipfs"
      }
    }
  }
});
var ConfigService = class _ConfigService extends Context.Tag("ConfigService")() {
  static layerMemory = Layer.effect(_ConfigService, Effect.succeed(memoryConfig));
  static fromConfig = (config) => Layer.succeed(_ConfigService, config);
  static load = (args) => {
    const defaultConfigPath = getProfileConfigPath(DX_CONFIG, args.profile);
    return Effect.gen(function* () {
      const fs = yield* FileSystem.FileSystem;
      const configPath = Option.getOrElse(args.config, () => defaultConfigPath);
      const configContent = yield* fs.readFileString(configPath);
      const configValues = Yaml.parse(configContent);
      return _ConfigService.of(new Config(configValues, profileBuiltinDefaults(args.profile).values));
    }).pipe(
      // If the config file doesn't exist, create it.
      Effect.catchTag("SystemError", () => Effect.gen(function* () {
        const configValues = defaultConfig.values;
        const fs = yield* FileSystem.FileSystem;
        const pathToCreate = Option.getOrElse(args.config, () => defaultConfigPath);
        yield* fs.makeDirectory(dirname(pathToCreate), {
          recursive: true
        });
        yield* fs.writeFileString(pathToCreate, Yaml.stringify(configValues));
        return _ConfigService.of(new Config(configValues));
      }))
    );
  };
};
var profileBuiltinDefaults = (profile) => {
  invariant(!profile.endsWith(".yml"), void 0, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 86, S: void 0, A: ["!profile.endsWith('.yml')", ""] });
  return new Config({
    runtime: {
      client: {
        edgeFeatures: {
          echoReplicator: true,
          feedReplicator: true,
          signaling: true,
          agents: true
        },
        storage: {
          persistent: true,
          dataRoot: getProfilePath(DX_DATA, profile)
        }
      }
    }
  });
};

// src/edge-services.ts
var EdgeServiceName = Object.freeze({
  Calls: "calls",
  Image: "image",
  Transcription: "transcription",
  Discord: "discord",
  CorsProxy: "cors-proxy",
  ApiProxy: "api-proxy",
  Introspect: "introspect",
  ChatAgent: "chat-agent"
});
var EDGE_SERVICE_DEFAULTS = Object.freeze({
  [EdgeServiceName.Calls]: "https://calls-service.dxos.workers.dev",
  [EdgeServiceName.Image]: "https://image-service-main.dxos.workers.dev",
  [EdgeServiceName.Transcription]: "https://calls-service.dxos.workers.dev",
  [EdgeServiceName.Discord]: "https://discord-service.dxos.workers.dev",
  [EdgeServiceName.CorsProxy]: "https://cors-proxy.dxos.workers.dev",
  [EdgeServiceName.ApiProxy]: "https://api-proxy.dxos.workers.dev",
  [EdgeServiceName.Introspect]: "https://introspect-service-labs.dxos.workers.dev/mcp",
  [EdgeServiceName.ChatAgent]: "wss://chat-agent-labs.dxos.workers.dev"
});
var getEdgeServiceEndpoint = (config, name) => config.values.runtime?.services?.edgeServices?.findLast((service) => service.name === name)?.endpoint ?? EDGE_SERVICE_DEFAULTS[name];

// src/index.ts
export * from "#loaders";
export * from "#savers";
export * from "#plugin";

// src/telemetry.ts
import { isNode } from "@dxos/util";
var resolveTelemetryTag = (config) => {
  return config?.get("runtime.app.env.DX_TELEMETRY_TAG") ?? (isNode() ? process.env.DX_TELEMETRY_TAG : void 0);
};

// src/preset.ts
import * as Match from "effect/Match";
var edgeUrl = (edge) => Match.value(edge).pipe(Match.when("local", () => "http://localhost:8787"), Match.when("dev", () => "https://edge.dxos.workers.dev"), Match.when("main", () => "https://edge-main.dxos.workers.dev"), Match.when("production", () => "https://edge-production.dxos.workers.dev"), Match.exhaustive);
var sandboxUrl = (sandbox) => Match.value(sandbox).pipe(Match.when("local", () => "http://localhost:8792"), Match.when("dev", () => "https://sandbox-service.dxos.workers.dev"), Match.when("main", () => "https://sandbox-service.dxos.workers.dev"), Match.when("production", () => "https://sandbox-service.dxos.workers.dev"), Match.exhaustive);
var configPreset = ({ edge = "main", sandbox } = {}) => new Config({
  version: 1,
  runtime: {
    client: {
      edgeFeatures: {
        signaling: true,
        echoReplicator: true,
        feedReplicator: true
      }
    },
    services: {
      edge: {
        url: edgeUrl(edge)
      },
      ...sandbox ? {
        sandbox: {
          url: sandboxUrl(sandbox)
        }
      } : {}
    }
  }
});
export {
  Config,
  ConfigResource,
  ConfigService,
  EDGE_SERVICE_DEFAULTS,
  EdgeServiceName,
  FILE_DEFAULTS,
  FILE_DYNAMICS,
  FILE_ENVS,
  configPreset,
  defaultConfig,
  defs,
  getEdgeServiceEndpoint,
  mapFromKeyValues,
  mapToKeyValues,
  memoryConfig,
  resolveTelemetryTag,
  validateConfig
};
//# sourceMappingURL=index.mjs.map
